import { Controller } from "@hotwired/stimulus"

const MAX_TOASTS = 5

export default class extends Controller {
  static targets = ["toast"]

  initialize() {
    // Set up before connect — Stimulus may fire toastTargetConnected for
    // pre-existing toasts (e.g. flash messages) before connect() runs.
    this.timers = new Map()
  }

  connect() {
    // Animate in any toasts already in the DOM (e.g. from flash messages)
    this.toastTargets.forEach((el) => this.initToast(el))
  }

  disconnect() {
    this.timers.forEach((timer) => clearTimeout(timer))
    this.timers.clear()
  }

  // Called automatically by Stimulus when a new toast target appears
  toastTargetConnected(el) {
    this.initToast(el)
  }

  toastTargetDisconnected(el) {
    const timer = this.timers.get(el)
    if (timer) {
      clearTimeout(timer)
      this.timers.delete(el)
    }
  }

  initToast(el) {
    // Enforce max stacking limit - remove oldest first
    this.enforceLimit()

    // Slide in from the right
    requestAnimationFrame(() => {
      el.classList.add("app-toast--visible")
    })

    // Set up auto-dismiss if configured
    if (el.dataset.autoDismiss === "true") {
      const duration = parseInt(el.dataset.duration, 10) || 4000
      this.startAutoDismiss(el, duration)

      // Pause on hover
      el.addEventListener("mouseenter", () => this.pauseAutoDismiss(el))
      el.addEventListener("mouseleave", () => this.resumeAutoDismiss(el))
    }
  }

  startAutoDismiss(el, duration) {
    const bar = el.querySelector(".app-toast__progress-bar")
    if (bar) {
      bar.style.transition = `width ${duration}ms linear`
      requestAnimationFrame(() => {
        bar.style.width = "0%"
      })
    }

    el._dismissDuration = duration
    el._dismissStart = Date.now()

    const timer = setTimeout(() => this.removeToast(el), duration)
    this.timers.set(el, timer)
  }

  pauseAutoDismiss(el) {
    const timer = this.timers.get(el)
    if (!timer) return

    clearTimeout(timer)
    this.timers.delete(el)

    // Calculate remaining time
    const elapsed = Date.now() - el._dismissStart
    el._dismissRemaining = (el._dismissDuration || 4000) - elapsed

    // Pause the progress bar
    const bar = el.querySelector(".app-toast__progress-bar")
    if (bar) {
      const computed = window.getComputedStyle(bar)
      bar.style.transition = "none"
      bar.style.width = computed.width
    }
  }

  resumeAutoDismiss(el) {
    const remaining = el._dismissRemaining
    if (!remaining || remaining <= 0) {
      this.removeToast(el)
      return
    }

    const bar = el.querySelector(".app-toast__progress-bar")
    if (bar) {
      requestAnimationFrame(() => {
        bar.style.transition = `width ${remaining}ms linear`
        bar.style.width = "0%"
      })
    }

    el._dismissStart = Date.now()
    el._dismissDuration = remaining

    const timer = setTimeout(() => this.removeToast(el), remaining)
    this.timers.set(el, timer)
  }

  // Action wired to the close button
  dismiss(event) {
    const toast = event.target.closest("[data-toast-manager-target='toast']")
    if (toast) this.removeToast(toast)
  }

  removeToast(el) {
    const timer = this.timers.get(el)
    if (timer) {
      clearTimeout(timer)
      this.timers.delete(el)
    }

    el.classList.add("app-toast--exiting")
    el.addEventListener("animationend", () => el.remove(), { once: true })

    // Fallback removal if animationend doesn't fire
    setTimeout(() => {
      if (el.parentNode) el.remove()
    }, 400)
  }

  enforceLimit() {
    const toasts = this.toastTargets
    while (toasts.length > MAX_TOASTS) {
      this.removeToast(toasts[0])
    }
  }
}
